import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type MouseEvent,
} from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { fadeIn } from 'app/animations';
import { shadow } from 'app/colors';
import { borderRadii, gutters } from 'app/spacing';
import { fontWeights, KizenTypography } from '__app/typography';
import {
  ExpandableMonthCalendarCell,
  MonthCalendarCellHeader,
  isCellInLastRow,
} from '__components/Calendar';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import IconButton from '__components/Kizen/IconButton';
import ScrollFadeContainer from '__components/Kizen/ScrollFadeContainer';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import { WEEKDAYS_SHORT } from 'utility/datetime';
import { useTranslation } from 'react-i18next';
import { ScheduleActivityButton } from './ScheduleActivityButton';
import { ActivityPill } from './ActivityPill';
import { CALENDAR_VIEW_OPTIONS } from 'components/Calendar/constants';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { OverflowPortal, useOverflowPortal } from 'components/OverflowPortal';
import type { CalendarItem, ScheduledActivity } from '../types';
import { ExternalCalendarEventPill } from './common';

const EVENT_PILL_HEIGHT = 26;
const COLLAPSED_HEADER_HEIGHT = 24;
const EXPANDED_HEADER_HEIGHT = 57;
const MAX_EVENTS_EXPANDED = 10;
const TRANSITION_DURATION = 300;
const WIDTH_EXPANSION = 60;
const headerIconColor = {
  default: 'var(--color-background-dark)',
  hover: 'var(--color-background-darker)',
};

const EventsContainer = styled.div<{
  expanded: boolean;
  rowHeight: number;
  expansion: [number, number];
}>`
  height: 100%;
  ${({ expanded, rowHeight, expansion }) =>
    expanded
      ? css`
          max-height: ${rowHeight +
          expansion[1] -
          EXPANDED_HEADER_HEIGHT +
          10}px;
          padding-bottom: 5px;
        `
      : css`
          max-height: ${rowHeight - COLLAPSED_HEADER_HEIGHT}px;
          padding-bottom: ${gutters.spacing()}px;
        `}
`;

const EventsScrollContainer = styled(ScrollFadeContainer)<{
  expanded: boolean;
}>`
  overflow: overlay;
  overflow: auto;
  height: 99%;
  width: 100%;
  flex-shrink: 0;
  padding-left: ${gutters.spacing()}px;

  ${({ expanded }) =>
    expanded &&
    css`
      padding-left: ${gutters.spacing(2)}px;
      padding-right: ${gutters.spacing()}px;
    `}

  > div {
    width: 100%;
  }
`;

const PillsContainer = styled.div`
  width: 100%;
  padding-right: 2px;
  box-sizing: border-box;
  ${({ expanded }: { expanded?: boolean }) =>
    expanded
      ? css`
          padding-right: 0;
        `
      : ''}
`;

const EventsScrollContainerWrapper = styled.div`
  display: flex;
  height: 100%;
`;

const MonthCellHeader = styled(MonthCalendarCellHeader)<{ expanded: boolean }>`
  justify-content: space-between;
  align-items: center;
  opacity: 1;
  ${({ expanded }) =>
    expanded &&
    css`
      opacity: 0;
      transition: opacity ${TRANSITION_DURATION}ms;
    `}
`;

const SelectableDateCell = styled(ExpandableMonthCalendarCell)<{
  expanded: boolean;
  skipped?: boolean;
}>`
  ${({ skipped }) =>
    skipped
      ? css`
          background-image: repeating-linear-gradient(
            -35deg,
            transparent 10px,
            var(--color-background-medium) 11px,
            transparent 12px,
            transparent 20px
          );
          & > div > p {
            color: var(--color-font-placeholder);
          }
        `
      : ''}
  ${({ expanded }) =>
    expanded &&
    css`
      ${shadow}
      border-radius: ${borderRadii.small};
      z-index: 1000;
      position: relative;
    `}
`;

const ExpandedMonthCellHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${gutters.spacing(2)}px;
  animation: ${fadeIn} ${TRANSITION_DURATION}ms ease-in;
`;

const PortalAnimationWrapper = styled.div`
  animation: ${fadeIn} ${TRANSITION_DURATION}ms ease-in;
`;

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  right: ${gutters.spacing(2)}px;
  top: ${gutters.spacing(2)}px;
`;

const ExpandedDayOfMonth = styled(KizenTypography)`
  font-weight: ${fontWeights.bold};
  margin-top: ${gutters.spacing()}px;
`;

const StyledGradient = styled(Gradient)<{ color: string }>`
  z-index: 1;
`;

interface BuildMonthCellProps {
  activitiesLookup: Record<string, (ScheduledActivity | CalendarItem)[]>;
  onActivityClick: (activity: ScheduledActivity) => void;
  onActivityAction: (activity: ScheduledActivity, action: string) => void;
  onAddActivity: (date: Date) => void;
  canEdit: boolean;
  timezone: string;
  businessTimezone: string;
  loading?: boolean;
  skipLookup?: Record<string, boolean>;
  skipWeekdays?: number[];
  fadeLength?: number;
  isMobile?: boolean;
}

export const buildMonthCell =
  ({
    activitiesLookup,
    onActivityClick,
    onActivityAction,
    onAddActivity,
    canEdit,
    timezone,
    businessTimezone,
    loading = false,
    skipLookup = {},
    skipWeekdays = [],
    fadeLength = 10,
    isMobile,
  }: BuildMonthCellProps) =>
  ({
    date,
    calendarMonth,
    calendarYear,
    rowHeight,
    index,
    total,
    numColumns,
    ...rest
  }: any) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(false);
    const [transitionComplete, setTransitionComplete] = useState(false);
    const [showAddButton, setShowAddButton] = useState(false);
    const cellRef = useRef<HTMLDivElement>(null);
    const portalContainer = useOverflowPortal();

    const dateKey = date.toDateString();
    const activities = activitiesLookup[dateKey] || [];
    const columnIndex = index % numColumns;
    const skipped = skipLookup[dateKey];
    const skippedByWeekday = skipWeekdays.includes(date.getDay());
    const isSkipped =
      skipped === true || (skipped !== false && skippedByWeekday);

    const activityPills = activities.map((activity) => {
      if (activity.isCalendarEvent) {
        return (
          <ExternalCalendarEventPill
            key={activity.id}
            id={activity.id}
            date={activity.date}
            isSpanning={activity.isSpanning}
            disableHoverEffect
            customColor="var(--color-viz-cat-1-primary)"
            isPastDate={activity.dueDatetime < Date.now()}
            url={activity.url}
            columnIndex={columnIndex}
            totalColumns={numColumns}
            event={activity}
          >
            {activity.name}
          </ExternalCalendarEventPill>
        );
      }

      return (
        <ActivityPill
          key={activity.id}
          activity={activity}
          timezone={timezone}
          businessTimezone={businessTimezone}
          onComplete={onActivityClick}
          onEdit={(act) => onActivityAction(act, 'edit')}
          onDelete={(id) =>
            onActivityAction(
              activities.find(
                (a) => a.id === id && !a.isCalendarEvent
              ) as ScheduledActivity,
              'delete'
            )
          }
          canEdit={canEdit}
          columnIndex={columnIndex}
          totalColumns={numColumns}
          isMobile={isMobile}
          // Disable the hover effect on the pill since we do it with Javascript in order to highlight all pills with the same activity id across cells
          disableHoverEffect
        />
      );
    });

    const isLastRow = isCellInLastRow(index, total, numColumns);
    const scrollable = selected
      ? activityPills.length > MAX_EVENTS_EXPANDED
      : activityPills.length * EVENT_PILL_HEIGHT + COLLAPSED_HEADER_HEIGHT >
        rowHeight;
    const heightExpansion =
      EVENT_PILL_HEIGHT * Math.min(MAX_EVENTS_EXPANDED, activityPills.length) +
      EXPANDED_HEADER_HEIGHT +
      gutters.spacing() -
      rowHeight;
    const expansion: [number, number] = [
      WIDTH_EXPANSION,
      Math.max(WIDTH_EXPANSION / 2, heightExpansion),
    ];
    const gradientColor =
      date.getMonth() === calendarMonth
        ? 'var(--color-background-white)'
        : 'var(--color-background-light)';

    const handleAddClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        onAddActivity(date);
      },
      [date]
    );

    const handleCellMouseEnter = useCallback(() => {
      if (!selected) {
        setShowAddButton(true);
      }
    }, [selected]);

    const handleCellMouseLeave = useCallback(() => {
      setShowAddButton(false);
    }, []);

    useEffect(() => {
      // Close expanded cell when clicking outside
      const listener = () => {
        if (selected) {
          setSelected(false);
        }
      };
      window.addEventListener('click', listener);
      return () => window.removeEventListener('click', listener);
    }, [selected]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const scrolled = useEndsOfScroll(scrollRef, [
      activities,
      scrollable,
      selected,
      expansion,
    ]);

    const open = (ev: MouseEvent) => {
      ev.stopPropagation();
      setSelected(true);
      setTimeout(() => setTransitionComplete(true), TRANSITION_DURATION);
    };

    const close = (ev: MouseEvent) => {
      ev.stopPropagation();
      setSelected(false);
      setShowAddButton(false);
      setTimeout(() => setTransitionComplete(false), 10);
    };

    const cellContent = (
      <>
        {canEdit && !selected ? (
          <ScheduleActivityButton
            view={CALENDAR_VIEW_OPTIONS.month}
            show={showAddButton}
            onClick={handleAddClick}
          />
        ) : null}
        {(!selected || !transitionComplete) && (
          <MonthCellHeader
            className=""
            date={date}
            calendarMonth={calendarMonth}
            expanded={selected}
          >
            {scrollable && (
              <IconButton
                sizing="dense"
                title={t('Expand')}
                color={headerIconColor}
                disabled={loading}
                onClick={open}
              >
                <IconSizing size="10px">
                  <Icon icon="external-link" />
                </IconSizing>
              </IconButton>
            )}
          </MonthCellHeader>
        )}
        {selected && transitionComplete && (
          <ExpandedMonthCellHeader>
            <StyledCloseButton
              sizing="dense"
              color={headerIconColor}
              title={t('Close')}
            >
              <IconSizing size="12px">
                <Icon icon="close-cross" onClick={close} />
              </IconSizing>
            </StyledCloseButton>
            <KizenTypography>
              {WEEKDAYS_SHORT[date.getDay()].toUpperCase()}
            </KizenTypography>
            <ExpandedDayOfMonth type="subheader">
              {date.getDate()}
            </ExpandedDayOfMonth>
          </ExpandedMonthCellHeader>
        )}
        <EventsContainer
          expanded={selected}
          rowHeight={rowHeight}
          expansion={expansion}
        >
          <EventsScrollContainerWrapper>
            <EventsScrollContainer
              className="kds-expanding-scrollbar"
              ref={scrollRef}
              scrolled={scrolled}
              gradient={<StyledGradient color={gradientColor} />}
              top={scrollable ? { fadeLength } : false}
              bottom={scrollable ? { fadeLength } : false}
              expanded={selected}
            >
              <PillsContainer expanded={selected}>
                {activityPills}
              </PillsContainer>
            </EventsScrollContainer>
          </EventsScrollContainerWrapper>
        </EventsContainer>
      </>
    );

    if (!selected || !portalContainer) {
      return (
        <SelectableDateCell
          ref={cellRef}
          index={index}
          total={total}
          numColumns={numColumns}
          expanded={selected}
          expansion={expansion}
          translation={[-WIDTH_EXPANSION / 2, -WIDTH_EXPANSION / 2]}
          date={date}
          calendarMonth={calendarMonth}
          calendarYear={calendarYear}
          rowHeight={rowHeight}
          pinBottom={isLastRow}
          skipped={isSkipped}
          {...rest}
          onMouseEnter={handleCellMouseEnter}
          onMouseLeave={handleCellMouseLeave}
        >
          {cellContent}
        </SelectableDateCell>
      );
    }

    return (
      <>
        <div
          ref={cellRef}
          style={{ position: 'relative', width: '100%', height: '100%' }}
        >
          <SelectableDateCell
            index={index}
            total={total}
            numColumns={numColumns}
            expanded={false}
            expansion={expansion}
            translation={[-WIDTH_EXPANSION / 2, -WIDTH_EXPANSION / 2]}
            date={date}
            calendarMonth={calendarMonth}
            calendarYear={calendarYear}
            rowHeight={rowHeight}
            pinBottom={isLastRow}
            skipped={isSkipped}
            {...rest}
          />
        </div>
        <OverflowPortal triggerRef={cellRef} enabled={true}>
          <PortalAnimationWrapper>
            <SelectableDateCell
              index={index}
              total={total}
              numColumns={numColumns}
              expanded={true}
              expansion={expansion}
              translation={[-WIDTH_EXPANSION / 2, -WIDTH_EXPANSION / 2]}
              date={date}
              calendarMonth={calendarMonth}
              calendarYear={calendarYear}
              rowHeight={rowHeight}
              pinBottom={isLastRow}
              skipped={isSkipped}
              {...rest}
              onMouseEnter={handleCellMouseEnter}
              onMouseLeave={handleCellMouseLeave}
            >
              {cellContent}
            </SelectableDateCell>
          </PortalAnimationWrapper>
        </OverflowPortal>
      </>
    );
  };
